// Sample-data registry for preview mode.
//
// Maps a URL fragment (matched as a substring of the request URL) to the JSON
// body the mock network layer returns. ALL data here is fictitious — UI shell
// only, never real employee or customer data.
//
// To preview a feature that needs specific data on screen, add ONE entry to
// FIXTURES keyed by a unique fragment of that endpoint's URL, with a body in
// the shape the endpoint normally returns. Anything not listed falls through
// to DEFAULT_RESPONSE so the page renders its empty state instead of spinning.

import { MOCK_USER_INFO } from "./previewMode";

// Returned for any BFF call with no explicit fixture. Permissive shape that
// satisfies both array-expecting list consumers and {status,data}-expecting
// readers without crashing a render.
const DEFAULT_RESPONSE = { status: "Success", data: [], message: "preview" };

// Dates anchored to "today" so the Fantasy admin scoring-freshness pill renders
// its green "Fresh" state in the preview. All names/numbers below are fictitious.
const PREVIEW_TODAY = new Date().toISOString().slice(0, 10);
const PREVIEW_YDAY = new Date(Date.now() - 86400000).toISOString().slice(0, 10);

// Glitch thread demo (thread #3099): a G-numbered glitch whose report, Kai
// handoff, verify result, a notify reply and status changes render as one
// thread. Assigned to the mock user so "My Glitches" opens it in the full
// action mode (status picker, Send & Notify). Fictitious names only.
const glitchAgo = (mins) => new Date(Date.now() - mins * 60000).toISOString();
const PREVIEW_GLITCH_THREAD = {
  id: 651, threadNumber: 3101, gid: "G-3101",
  title: "Stats page shows yesterday's numbers", severity: "High", area: "Stats",
  status: "Needs Input", submitterName: "Alfonso R", submitterRecid: "recAlfonso01",
  submitterPosition: "Sales Rep", submitterLocation: "Houston",
  assigneeRecid: "recPREVIEW0001", assigneeName: "Preview User",
  page: "/p/stats", device: "iPhone · Safari",
  createdAt: glitchAgo(60 * 26),
  description: "My stats still show yesterday's sales after I closed two deals this morning.\nRefreshing doesn't help.",
  kaiPushFix: true,
  notes: [
    { id: 9001, responseNo: 2, gid: "G-3101-2", kind: "response", parentId: null, authorName: "Levi L",
      body: "Thanks — sending this to Kai with Push Fix.", createdAt: glitchAgo(60 * 25) },
    { id: 9002, responseNo: 3, gid: "G-3101-3", kind: "handoff", parentId: null, authorName: "Kai",
      body: "Handoff: the stats cache refreshed only at 2am. PR #1234 (merged) refreshes it after every sale sync.\nHow to test: close a sale, open Stats within 5 minutes, the count includes it.",
      createdAt: glitchAgo(60 * 6) },
    { id: 9003, responseNo: 4, gid: "G-3101-4", kind: "verify", parentId: 9002, parentResponseNo: 3, authorName: "Kai",
      body: "Verify (tier 3): the count updates for Houston but not for Detroit reps. Needs Input — is the Detroit sale sync supposed to run hourly?",
      createdAt: glitchAgo(60 * 2) },
    { id: 9004, responseNo: 5, gid: "G-3101-5", kind: "response", parentId: 9003, parentResponseNo: 4, authorName: "Alfonso R",
      body: "Mine updated this time, thank you!", replyToNotify: true, notifiedAt: null, createdAt: glitchAgo(35) },
  ],
  statusEvents: [
    { oldStatus: null, newStatus: "In Progress", changedBy: "Levi L", changedAt: glitchAgo(60 * 25 - 1) },
    { oldStatus: "In Progress", newStatus: "Needs Input", changedBy: "Kai (verify tier 3)", changedAt: glitchAgo(60 * 2 - 1) },
  ],
};

const FIXTURES = {
  // --- Add per-feature fixtures below this line --------------------------
  // Workflow page filter facets (feature #64) — Position dropdown + Individual
  // picker options. Keyed so it never collides with the "/list" branch above.
  "workflow/portal/filters": {
    status: "Success",
    data: {
      positions: ["Branch Manager", "President", "Retention Specialist", "Route Manager"],
      creators: [
        { recId: "recPREVAvery", name: "Avery Stone", position: "Retention Specialist", location: "Detroit" },
        { recId: "recPREVJamie", name: "Jamie Carter", position: "Branch Manager", location: "Detroit" },
        { recId: "recPREVLevi", name: "Levi Leatham", position: "President", location: "Houston" },
        { recId: "recPREVMorgan", name: "Morgan Diaz", position: "Route Manager", location: "Houston" },
      ],
  // Workflow page (Feature #68 — Duplicate & Modify). The list renders three
  // saved workflows, each with a "Duplicate" (copy) button. Fictitious data.
  // #54 Workflow Templates Library — the Workflow page landing list. Two sample
  // saved workflows so the list renders (with the "Create" button that opens the
  // new template chooser) instead of the empty state. All fictitious; UI shell.
  // #72 Time-delay & wait-for-condition visualizers. The Workflow preview's
  // saved-list + the sample workflow detail, whose stages include a `delay`
  // and a `wait_condition` so WorkflowPreview renders the new visual cards
  // (WorkflowDelayCard / WorkflowConditionCard) between the normal steps.
  // WorkflowContent lands directly on this workflow in preview mode. All
  // fictitious — UI shell only.
  "workflow/portal/list": {
    status: "Success",
    data: [
      {
        id: 101,
        title: "New Rep Agreement Signing",
        status: "active",
        createdAt: new Date(Date.now() - 9 * 86400000).toISOString(),
        updatedAt: new Date(Date.now() - 2 * 86400000).toISOString(),
      },
      {
        id: 102,
        title: "Customer Welcome Sequence",
        status: "ready",
        createdAt: new Date(Date.now() - 5 * 86400000).toISOString(),
        updatedAt: new Date(Date.now() - 1 * 86400000).toISOString(),
      },
      {
        id: 103,
        title: "Monthly Inventory Reminder",
        status: "queued",
        createdAt: new Date(Date.now() - 3 * 86400000).toISOString(),
        updatedAt: new Date(Date.now() - 3 * 86400000).toISOString(),
      },
    ],
  },
  // The duplicate endpoint's response — the freshly-cloned "Copy of …" draft.
  "/duplicate": {
    status: "Success",
    message: "Workflow duplicated.",
    data: {
      id: 999,
      title: "Copy of New Rep Agreement Signing",
      scribble:
        "When a new rep is hired, send them the rep agreement to sign. After " +
        "they sign, the VP of Sales signs it, then everyone gets a copy and it's filed.",
      status: "queued",
    },
  },
  // The cloned copy's detail page (opened right after Duplicate). Shows the
  // "Creating…" state because the brain is regenerating the preview for the copy.
  "workflow/portal/999": {
    status: "Success",
    data: {
      id: 999,
      title: "Copy of New Rep Agreement Signing",
      scribble:
        "When a new rep is hired, send them the rep agreement to sign. After " +
        "they sign, the VP of Sales signs it, then everyone gets a copy and it's filed.",
      status: "queued",
      preview: null,
      prereqs: [],
      messages: [],
    },
  },
  // Feature #63 — Workflow compliance validator. Sample flags (one red blocker +
  // one yellow warning) so the WorkflowCompliance panel renders its full state
  // in a preview build. All fictitious — illustrates the gate, not real data.
  "workflow/portal/compliance-check": {
    status: "Success",
    data: {
      flags: [
        {
          severity: "red",
          message:
            "This workflow appears to write to the Employees Airtable base (or the " +
            "termination/Final Status field), which controls pay and PestRoutes " +
            "access and can auto-terminate an employee within ~15 minutes. Employee " +
            "writes must go through a reviewed path with Levi's sign-off.",
          business_map_ref: "business_map/areas/hr-payroll/processes/employee-lifecycle.md",
        },
        {
          severity: "yellow",
          message:
            "Portal login and PestRoutes access are blocked until BOTH the background " +
            "check and the drug test pass. Make sure those screening steps run and " +
            "clear before access is granted.",
          business_map_ref: "business_map/areas/hr-payroll/processes/onboarding.md",
        },
      ],
    },
  },
  // Workflow Dry Run / Test Mode (#57) — substring matches BOTH the POST that
  // starts a dry run AND the GET poll, so the preview returns a finished
  // simulation immediately (status 'done' with steps). All fictitious.
  "workflow/portal/dry-run": {
    status: "Success",
    data: {
      id: 9001,
      description: "When a new rep is hired, send the rep agreement to sign, then the VP signs, then file it.",
      testEmployeeRecId: null,
      status: "done",
      result: {
        steps: [
          { name: "New hire detected", status: "would_pass", reason: "Trigger matched a sample new-hire record.", simulated_output: "Test Employee (Sales Rep, Houston) queued." },
          { name: "Send rep agreement to sign", status: "would_pass", reason: "Agreement template found and field-mapped.", simulated_output: "Email to test@example.com with signing link (not actually sent)." },
          { name: "Rep signs", status: "would_pass", reason: "Signature step routes back to the creator in test mode.", simulated_output: "Signed copy generated with sample data." },
          { name: "VP of Sales signs", status: "would_fail", reason: "No VP of Sales is assigned for this market.", simulated_output: "Would block here until a VP signer is set." },
          { name: "File completed document", status: "would_pass", reason: "Destination folder resolved.", simulated_output: "Would file to Drive › Rep Agreements (skipped in test)." },
        ],
      },
      createdAt: PREVIEW_TODAY,
      updatedAt: PREVIEW_TODAY,
    },
  },
  // Test-employee picker source (reused mirror-backed recipients endpoint).
  "balance/portal/recipients": {
    status: "Success",
    data: [
      { recId: "recPrev01", name: "Alex Morgan", role: "Sales Rep", market: "Houston" },
      { recId: "recPrev02", name: "Jamie Rivera", role: "Manager", market: "Detroit" },
      { recId: "recPrev03", name: "Sam Carter", role: "Sales Rep", market: "Houston" },
    ],
  },
  // Workflow page — Integration Health status bar (feature #67). All services
  // "up" so the preview shows the healthy green-dot state.
  "workflow/portal/integration-health": {
    status: "Success",
    data: {
      services: [
        { name: "Airtable", status: "up", latency_ms: 142, last_checked: new Date().toISOString(), detail: "Reachable" },
        { name: "Fillout", status: "up", latency_ms: 263, last_checked: new Date().toISOString(), detail: "Reachable" },
        { name: "n8n", status: "up", latency_ms: 88, last_checked: new Date().toISOString(), detail: "Reachable" },
        { name: "DocuSign", status: "up", latency_ms: 311, last_checked: new Date().toISOString(), detail: "Reachable" },
        { name: "GCP", status: "up", latency_ms: 0, last_checked: new Date().toISOString(), detail: "BFF serving" },
      ],
      generated_at: new Date().toISOString(),
      cached: false,
    },
  },
      },
  },
  // Matches GET /workflow/portal/wf72 and .../wf72/status (same shape).
  "workflow/portal/wf72": {
    status: "Success",
    data: {
      id: "wf72",
      title: "New Rep Agreement — signing flow",
      status: "ready",
      preview: {
        summary:
          "A new rep gets their agreement to sign, we give them a few days, then wait for the signed copy before looping in the VP.",
        stages: [
          {
            actor: "Rep",
            title: "Receives the agreement",
            detail: "Texted a secure link the moment HR marks them hired.",
            timing: "Right after hire",
          },
          {
            type: "delay",
            duration: "3 days",
            unit: "days",
            count: 3,
          },
          {
            actor: "Rep",
            title: "Gets a friendly reminder",
            detail: "An automatic nudge with the same link if they haven't signed.",
            timing: "Day 3",
          },
          {
            type: "wait_condition",
            field: "Airtable Rep Summer.Status",
            operator: "equals",
            value: "Signed",
            polling: "n8n webhook",
          },
          {
            actor: "VP of Sales",
            title: "Counter-signs",
            detail: "Asked to sign the moment the rep's signature lands.",
            timing: "Within minutes of signing",
          },
          {
            actor: "HR",
            title: "Gets the signed copy on file",
            detail: "The finished PDF is filed on the rep's record and they get a copy.",
            timing: "Immediately after counter-sign",
          },
        ],
      },
      prerequisites: [],
      messages: [],
      updatedAt: `${PREVIEW_TODAY}T09:30:00Z`,
      createdAt: `${PREVIEW_YDAY}T14:00:00Z`,
    },
  },

  // #73 Cost / Resource Estimation Warning. The preview build lands directly on
  // this sample workflow's Preview screen (PREVIEW_WORKFLOW_ID="wf-preview-73"),
  // so the red "High cost per run" badge shows under the summary. One key serves
  // both GET /workflow/portal/wf-preview-73 and …/status (substring match). All
  // fictitious — UI shell only.
  "workflow/portal/wf-preview-73": {
    status: "Success",
    data: {
      id: "wf-preview-73",
      title: "Personalized thank-you to every customer, nightly",
      scribble:
        "Every night, write a personalized thank-you note for each of our customers and email it to them.",
      status: "ready",
      preview: {
        summary:
          "Each night this looks at every customer and has the AI write them a personalized thank-you, then emails it.",
        stages: [
          {
            n: 1,
            actor: "System",
            title: "Gathers the customer list",
            detail: "Every night it pulls the full list of active customers.",
            timing: "Nightly at 9pm",
          },
          {
            n: 2,
            actor: "Kai (AI)",
            title: "Writes a personal note for each customer",
            detail:
              "For every single customer, the AI drafts a one-of-a-kind thank-you message.",
            timing: "Right after the list is gathered",
          },
          {
            n: 3,
            actor: "System",
            title: "Emails each note",
            detail: "Each customer gets their personalized note in their inbox.",
            timing: "As soon as each note is written",
          },
        ],
      },
      prereqs: [],
      costEstimate: {
        level: "high",
        details:
          "This workflow loops through every customer and asks the AI to write a brand-new message for each one, every single night — so the cost scales with how many customers you have.",
        ai_calls_per_run: 5000,
        note:
          "Warning: this workflow loops through ~5,000 records and makes ~1 AI call per record — estimated cost per run: High. Heads up: workflow creation is AI-heavy, but after build n8n handles execution, so most workflows are low runtime cost. This one is unusual because it calls the AI for every record on every run.",
      },
      messages: [],
      createdAt: new Date(Date.now() - 86400000).toISOString(),
      updatedAt: new Date().toISOString(),
    },
  },

  // #71 Automated Documentation Generation. Keyed BEFORE the workflow-detail
  // fixtures so the longer "generate-docs" / "…/status" URLs win the substring
  // match over the bare "workflow/portal/1" detail key. All sample only.
  "generate-docs": {
    status: "Success",
    data: {
      source: "ai",
      markdown: [
        "# New Lead Welcome Text",
        "",
        "_Workflow documentation_",
        "",
        "## Purpose & Trigger",
        "",
        "Automatically send a friendly welcome text to a brand-new lead the moment they're added, so no one falls through the cracks while the office is busy.",
        "",
        "**Trigger:** A new row is created in the Airtable *Leads* table.",
        "",
        "## Systems Touched",
        "",
        "- Airtable (Sales base — Leads table)",
        "- n8n (automation runner)",
        "- RingCentral (outbound SMS)",
        "- Portal (status logging)",
        "",
        "## Step-by-Step",
        "",
        "1. **New lead is added** — A rep or the website adds a lead to the Airtable Leads table.",
        "2. **n8n picks it up** — The automation notices the new lead within a minute.",
        "3. **Welcome text is drafted** — A short, personalized message is created using the lead's first name.",
        "4. **Text is sent** — RingCentral delivers the SMS to the lead's phone number.",
        "5. **Outcome is logged** — The lead's row is stamped 'Welcomed' so the team can see it went out.",
        "",
        "## Expected Outcomes",
        "",
        "Every new lead receives a welcome text within about a minute of being added, and the Leads table shows a clear record that it happened.",
        "",
        "## Prerequisites",
        "",
        "- **Phone number field** — The Leads table needs a valid mobile number on each lead for the text to send.",
        "  - Suggestion: Make the phone field required on the lead-intake form.",
      ].join("\n"),
    },
  },
  // Workflow status poll (feature #71 reads this to gate the docs button).
  "workflow/portal/1/status": {
    status: "Success",
    data: { id: 1, title: "New Lead Welcome Text", status: "active" },
  },
  // Workflow detail (drives WorkflowPreview so the docs panel renders beneath a
  // realistic, finalized workflow). Sample data only — UI shell.
  "workflow/portal/1": {
    status: "Success",
    data: {
      id: 1,
      title: "New Lead Welcome Text",
      scribble:
        "When a new lead is added, text them a quick friendly welcome with their first name so we never leave a lead hanging.",
      status: "active",
      preview: {
        summary:
          "Sends a personalized welcome text to every new lead within a minute of being added.",
        stages: [
          { title: "New lead is added", detail: "A rep or the website adds a lead to Airtable.", actor: "Sales rep / website", timing: "Anytime" },
          { title: "Automation picks it up", detail: "n8n notices the new lead.", actor: "n8n", timing: "Within ~1 minute" },
          { title: "Welcome text is drafted", detail: "A short message is personalized with the lead's first name.", actor: "Kai", timing: "Instant" },
          { title: "Text is sent", detail: "RingCentral delivers the SMS to the lead.", actor: "RingCentral", timing: "Instant" },
          { title: "Outcome is logged", detail: "The lead's row is marked 'Welcomed'.", actor: "Automation", timing: "Instant" },
        ],
      },
      prerequisites: [
        {
          title: "Phone number field",
          detail: "Each lead needs a valid mobile number for the text to send.",
          suggestion: "Make the phone field required on the lead-intake form.",
        },
      ],
      messages: [
        { id: 1, role: "user", text: "Make sure it uses their first name.", at: "2026-06-20T15:00:00Z" },
        { id: 2, role: "kai", text: "Done — the welcome text now greets each lead by first name.", at: "2026-06-20T15:00:05Z" },
      ],
    },
  },

  // Feature #69 — Dynamic Variables Explorer. Sample systems/tables/fields so the
  // right-side panel renders its accordion + copy buttons. All fictitious shape.
  "workflow/portal/variables": {
    status: "Success",
    data: {
      systems: [
        {
          name: "Employees",
          source: "airtable",
          tables: [
            {
              name: "Employee",
              fields: [
                { name: "First Name", column: "first_name", type: "singleLineText", token: "{Employees.first_name}" },
                { name: "Last Name", column: "last_name", type: "singleLineText", token: "{Employees.last_name}" },
                { name: "Position", column: "position", type: "multipleRecordLinks", token: "{Employees.position}" },
                { name: "Start Date", column: "start_date", type: "date", token: "{Employees.start_date}" },
              ],
            },
            {
              name: "Onboarding Tasks",
              fields: [
                { name: "Task Name", column: "task_name", type: "singleLineText", token: "{Employees.task_name}" },
                { name: "Status", column: "status", type: "singleSelect", token: "{Employees.status}" },
              ],
            },
          ],
        },
        {
          name: "Sales",
          source: "airtable",
          tables: [
            {
              name: "Leads",
              fields: [
                { name: "Lead Name", column: "lead_name", type: "singleLineText", token: "{Sales.lead_name}" },
                { name: "Stage", column: "stage", type: "singleSelect", token: "{Sales.stage}" },
              ],
            },
          ],
        },
        {
          name: "Portal Database (PestRoutes)",
          source: "mysql",
          tables: [
            {
              name: "Customers",
              table: "customer",
              fields: [
                { name: "fname", column: "fname", type: "", token: "{customer.fname}" },
                { name: "lname", column: "lname", type: "", token: "{customer.lname}" },
                { name: "email", column: "email", type: "", token: "{customer.email}" },
              ],
            },
            {
              name: "Appointments",
              table: "appointments",
              fields: [
                { name: "appointmentID", column: "appointmentID", type: "", token: "{appointments.appointmentID}" },
                { name: "date", column: "date", type: "", token: "{appointments.date}" },
              ],
            },
          ],
        },
      ],
      builtAt: PREVIEW_TODAY + "T00:00:00.000Z",
    },
  },
  // Glitches page — shows filters row + leaderboard with Alfonso on the board.
  "glitch/portal/list": {
    status: "Success",
    data: {
      open: [
        PREVIEW_GLITCH_THREAD,
        {
          id: 1, title: "Customers not in portal", severity: "Medium", area: "Stats",
          status: "In Progress", submitterName: "Alfonso R", submitterRecid: "recAlfonso01",
          submitterPosition: "Sales Rep", submitterLocation: "Houston",
          createdAt: new Date(Date.now() - 2 * 86400000).toISOString(),
          description: "Some customers not showing up in the portal list.", notes: [],
        },
        {
          id: 2, title: "Daily mini test not operational", severity: "High", area: "Other",
          status: null, submitterName: "Alfonso R", submitterRecid: "recAlfonso01",
          submitterPosition: "Sales Rep", submitterLocation: "Houston",
          createdAt: new Date(Date.now() - 5 * 86400000).toISOString(),
          description: "The daily mini quiz doesn't load.", notes: [],
        },
        {
          id: 3, title: "Tournament page UI glitched", severity: "Medium", area: "Tournament",
          status: null, submitterName: "Jamie K", submitterRecid: "recJamie01",
          submitterPosition: "Manager", submitterLocation: "Detroit",
          createdAt: new Date(Date.now() - 1 * 86400000).toISOString(),
          description: "Tournament bracket doesn't render on mobile.", notes: [],
        },
      ],
      resolved: [
        {
          id: 4, title: "Technician paychecks incorrect", severity: "High", area: "Payroll",
          status: "Fixed", submitterName: "Alfonso R", submitterRecid: "recAlfonso01",
          submitterPosition: "Sales Rep", submitterLocation: "Houston",
          createdAt: new Date(Date.now() - 10 * 86400000).toISOString(),
          description: "Paycheck amounts off by one week.", notes: [],
        },
        {
          id: 5, title: "The pinning feature doesn't work", severity: "Low", area: "Other",
          status: "Fixed", submitterName: "Jamie K", submitterRecid: "recJamie01",
          submitterPosition: "Manager", submitterLocation: "Detroit",
          createdAt: new Date(Date.now() - 15 * 86400000).toISOString(),
          description: "Pins not saving after session.", notes: [],
        },
        {
          id: 6, title: "Videos not grading", severity: "Medium", area: "Other",
          status: "Duplicate", submitterName: "Sam G", submitterRecid: "recSamG01",
          submitterPosition: "Sales Rep", submitterLocation: "Houston",
          createdAt: new Date(Date.now() - 20 * 86400000).toISOString(),
          description: "Grading score not updating after video.", notes: [],
        },
      ],
    },
  },

  // "My Glitches" (thread #3099 preview) — the mock user's own view.
  "glitch/portal/mine": {
    status: "Success",
    data: {
      open: [
        PREVIEW_GLITCH_THREAD,
        {
          id: 652, threadNumber: 3104, gid: "G-3104", title: "Points page slow to load", severity: "Low",
          area: "Points", status: null, submitterName: "Preview User", submitterRecid: "recPREVIEW0001",
          createdAt: glitchAgo(50), description: "Takes about 10 seconds to open.", notes: [], statusEvents: [],
        },
      ],
      resolved: [],
    },
  },

  // Payroll "Projected Payments" — demo the redesigned Backend 1 popup.
  // All numbers fictitious. Three BE1 rows show every outcome of the same
  // formula: underwater -> $0, a healthy payout, and a CVD-gated -> $0.
  "get-projected-earnings": {
    status: "Success",
    data: { accountOverview: {}, projectedPay: {}, payBreakdown: {} },
  },
  "get-projected-payments": {
    status: "Success",
    data: [
      {
        Season: "2026 - Spring",
        Type: "BE1",
        Amount: "0.00",
        "Pay Date": "2026-05-29",
        "Week number": "202618",
        description: "",
        "Points to Pay Cents": 0,
        "Calculation Details": {
          type: "BE1",
          lockedRevenue: 24000,
          commissionPercentage: 0.18,
          gpc: 4320,
          advances: 4350,
          rent: 0,
          miscAdvances: 0,
          cvd: 480,
          be1: 0,
        },
      },
      {
        Season: "2026 - Summer",
        Type: "BE1",
        Amount: "1925.00",
        "Pay Date": "2026-08-28",
        "Week number": "202635",
        description: "",
        "Points to Pay Cents": 0,
        "Calculation Details": {
          type: "BE1",
          lockedRevenue: 40000,
          commissionPercentage: 0.4,
          gpc: 16000,
          advances: 4000,
          rent: 300,
          miscAdvances: 0,
          cvd: 0,
          be1: 1925,
        },
      },
      {
        Season: "2026 - Fall",
        Type: "BE1",
        Amount: "0.00",
        "Pay Date": "2026-11-27",
        "Week number": "202653",
        description: "",
        "Points to Pay Cents": 0,
        "Calculation Details": {
          type: "BE1",
          lockedRevenue: 8000,
          commissionPercentage: 0.18,
          gpc: 1440,
          advances: 200,
          rent: 0,
          miscAdvances: 0,
          cvd: 600,
          be1: 0,
        },
      },
    ],
  },

  // #36 Fantasy scoring transparency. Drives the new "Scoring" tab: the legend
  // (point values mirror the BFF scoringLegend constants) + a sample roster with
  // ledger lines, including one rep with no lines yet to show the empty state.
  // All reps/values fictitious.
  "fantasy/portal/my-ledger": {
    status: "Success",
    data: {
      seasonId: "2026-Summer",
      drafted: true,
      legend: {
        rookieMultiplier: 1.4,
        metrics: [
          { metric: "dm", label: "Decision Makers", points: 1, unit: "per DM", blurb: "1 point for every decision-maker conversation your rep logs that day. High volume, low unit value — the bread and butter." },
          { metric: "clutch", label: "Clutch sale", points: 1, unit: "per serviced sale", blurb: "An after-hours / weekend sale that actually gets serviced. Closing when everyone else has clocked out." },
          { metric: "same_day", label: "Same-day service", points: 10, unit: "per service", blurb: "Sold AND serviced the same day. No waiting — instant value for the customer." },
          { metric: "big_sale", label: "$800+ contract", points: 20, unit: "per sale", blurb: "A sale with first-year contract value of $800 or more. Big tickets, big points." },
          { metric: "personal_best", label: "Personal best", points: "25/50/100", unit: "day / week / month", blurb: "Breaking a personal revenue record: 25 pts for a new best Day, 50 for a Week, 100 for a Month." },
          { metric: "pin_timing", label: "Pin timing", points: "2 / 3", unit: "early / late pin", blurb: "Grind bonus: 2 pts per pin dropped before noon, 3 pts per pin after 8:45pm (each rep's own market time)." },
          { metric: "streak", label: "Hot streak", points: 5, unit: "× streak length / day", blurb: "Beat yesterday's revenue again? 5 pts × how many days the streak is running — it escalates fast. One down day resets it." },
        ],
      },
      reps: [
        {
          slot: 1, repName: "Marcus Bell", repRecid: "recPREVIEW001", isRookie: false, rookieMultiplier: 1, priceCredits: 38, totalPoints: 49,
          lines: [
            { score_date: "2026-06-06", metric: "dm", raw_stat: 5, points: 5, running_total: 5 },
            { score_date: "2026-06-06", metric: "big_sale", raw_stat: 1, points: 20, running_total: 25 },
            { score_date: "2026-06-07", metric: "dm", raw_stat: 4, points: 4, running_total: 29 },
            { score_date: "2026-06-07", metric: "same_day", raw_stat: 1, points: 10, running_total: 39 },
            { score_date: "2026-06-07", metric: "streak", raw_stat: 2, points: 10, running_total: 49 },
          ],
        },
        {
          slot: 2, repName: "Tyler Nguyen", repRecid: "recPREVIEW002", isRookie: true, rookieMultiplier: 1.4, priceCredits: 14, totalPoints: 46.2,
          lines: [
            { score_date: "2026-06-06", metric: "dm", raw_stat: 4, points: 5.6, running_total: 5.6 },
            { score_date: "2026-06-06", metric: "clutch", raw_stat: 1, points: 1.4, running_total: 7 },
            { score_date: "2026-06-07", metric: "pin_timing", raw_stat: 3, points: 4.2, running_total: 11.2 },
            { score_date: "2026-06-07", metric: "personal_best", raw_stat: 1, points: 35, running_total: 46.2 },
          ],
        },
        {
          slot: 3, repName: "Sofia Ramirez", repRecid: "recPREVIEW003", isRookie: false, rookieMultiplier: 1, priceCredits: 30, totalPoints: 0,
          lines: [],
        },
        {
          slot: 4, repName: "Jordan Pope", repRecid: "recPREVIEW004", isRookie: true, rookieMultiplier: 1.4, priceCredits: 12, totalPoints: 14,
          lines: [
            { score_date: "2026-06-07", metric: "same_day", raw_stat: 1, points: 14, running_total: 14 },
          ],
        },
        {
          slot: 5, repName: "Andre Willis", repRecid: "recPREVIEW005", isRookie: false, rookieMultiplier: 1, priceCredits: 26, totalPoints: 31,
          lines: [
            { score_date: "2026-06-06", metric: "dm", raw_stat: 6, points: 6, running_total: 6 },
            { score_date: "2026-06-07", metric: "dm", raw_stat: 5, points: 5, running_total: 11 },
            { score_date: "2026-06-07", metric: "big_sale", raw_stat: 1, points: 20, running_total: 31 },
          ],
        },
      ],
    },
  },
  // #50 Rep-facing "you're a star" Spotlight. Drives the new Spotlight tab:
  // total fantasy points, managers who drafted the rep, anonymized ownership
  // rank, and a draft-price trend. All names/numbers fictitious; UI shell only.
  "fantasy/portal/my-ownership": {
    status: "Success",
    data: {
      seasonId: "2026-Summer",
      isRep: true,
      repName: "Preview Star Rep",
      isRookie: true,
      rookieMultiplier: 1.4,
      timesDrafted: 5,
      totalManagers: 6,
      ownershipPct: 83.3,
      ownershipRank: 2,
      poolSize: 8,
      currentPrice: 16,
      priceChange: 4,
      priceTrend: [
        { date: "2026-05-12", price: 12 },
        { date: "2026-05-19", price: 13 },
        { date: "2026-05-27", price: 14 },
        { date: "2026-06-02", price: 15 },
        { date: "2026-06-07", price: 16 },
      ],
      totalPoints: 187.4,
    },
  },
  // Feature #35 — Fantasy Sales League admin (#26 shell) preview data.
  "admin/dashboard": {
    status: "Success",
    data: {
      managers: 6,
      rostersLocked: 4,
      rostersNotLocked: 2,
      poolSize: 8,
      repsDrafted: 5,
      lastScoredDate: PREVIEW_TODAY,
      lastScoringRun: `${PREVIEW_TODAY}T09:45:00Z`,
      scoringCadence: "daily",
      salaryCap: 100,
      rosterSize: 5,
      rookieMultiplier: 1.4,
      seasonId: "2026-Summer",
      seasonWindow: { start: "2026-05-01", end: "2026-08-31" },
    },
  },
  // #33 Fantasy admin Activity log — fictitious sample feed so the tab renders
  // login-free in preview. UI shell only; no real employee/customer data.
  "fantasy/portal/admin/activity": {
    status: "Success",
    data: {
      seasonId: "2026-Summer",
      count: 5,
      events: [
        { type: "scoring_run", at: "2026-06-08T11:05:00", actor: "Daily engine", summary: "Scoring run — 42 lines for 14 reps (2026-06-06 → 2026-06-08)", detail: { lines: 42, reps: 14, points: 318.5 } },
        { type: "admin_action", at: "2026-06-07T16:20:00", actor: "Preview Admin", summary: "Preview Admin — overrode rep price", detail: { repName: "Sample Rep A", from: 22, to: 25 } },
        { type: "swap", at: "2026-06-07T09:12:00", actor: "Sample Manager B", summary: "Sample Manager B swapped slot 3 → Sample Rep C", detail: { slot: 3, repName: "Sample Rep C" } },
        { type: "draft", at: "2026-06-05T14:40:00", actor: "Sample Manager A", summary: "Sample Manager A locked a roster", detail: { managerId: 101 } },
        { type: "scoring_run", at: "2026-06-05T11:05:00", actor: "Daily engine", summary: "Scoring run — 28 lines for 12 reps (2026-06-04 → 2026-06-05)", detail: { lines: 28, reps: 12, points: 204 } },
      ],
    },
  },
  // Standings board (Total / Per Day / Week). One fixture serves all three
  // boards (the ?board= param does not change the URL match). Lets a preview
  // render the leaderboard + the board toggle.
  "fantasy/portal/standings": {
    status: "Success",
    data: {
      leagues: [],
      pastWinners: [],
      standings: [
        { manager_id: "m1", display_name: "Diana Cecilia", isMe: false, total_points: 2220.2, points_per_day: 61.7, points: 312.5 },
        { manager_id: "m2", display_name: "Preview User", isMe: true, total_points: 2169.0, points_per_day: 60.2, points: 305.1 },
        { manager_id: "m3", display_name: "Marcus Lee", isMe: false, total_points: 1980.4, points_per_day: 55.0, points: 288.9 },
        { manager_id: "m4", display_name: "Sam Rivera", isMe: false, total_points: 1750.8, points_per_day: 48.6, points: 240.3 },
      ],
    },
  },
  // Boosts / power-ups. Two boosts undrafted-state false so the cards render.
  "fantasy/portal/powerups": {
    status: "Success",
    data: {
      enabled: true,
      drafted: true,
      thisWeek: PREVIEW_TODAY,
      upcomingWeeks: [PREVIEW_TODAY],
      weeks: [{ weekStart: PREVIEW_YDAY }, { weekStart: PREVIEW_TODAY }],
      roster: [
        { rep_recid: "recPV01", rep_name: "Sample Rep One", is_rookie: 0 },
        { rep_recid: "recPV03", rep_name: "Sample Rep Three", is_rookie: 0 },
        { rep_recid: "recPV02", rep_name: "Sample Rep Two", is_rookie: 1 },
      ],
      powerups: [
        { code: "DOUBLE_WEEK", name: "Double Week", description: "Double one rostered rep's points for a chosen week.", remaining: 1, usesPerSeason: 1 },
        { code: "SHIELD_WEEK", name: "Shield Week", description: "Drop your worst finished week from the Per-Day board.", remaining: 1, usesPerSeason: 1 },
      ],
      applied: [],
    },
  },
  // Moves — trades + waiver wire. Empty-but-valid shapes so the Moves tab
  // renders its empty states (and the guide card above it) instead of crashing.
  "fantasy/portal/trades": {
    status: "Success",
    data: { incoming: [], outgoing: [], resolved: [] },
  },
  "fantasy/portal/rosters": {
    status: "Success",
    data: {
      managers: [
        { managerId: 2, display_name: "Preview User", roster: [] },
        {
          managerId: 3,
          display_name: "Marcus Lee",
          roster: [{ rep_recid: "recPV05", rep_name: "Sample Rep Five", is_rookie: 0, price_credits: 22 }],
        },
      ],
    },
  },
  "fantasy/portal/waiver": {
    status: "Success",
    data: {
      weekStart: PREVIEW_TODAY,
      myClaim: null,
      pool: [
        { rep_recid: "recPV05", rep_name: "Sample Rep Five", is_rookie: 0, price_credits: 22, hot_points_7d: 41 },
        { rep_recid: "recPV09", rep_name: "Sample Rep Nine", is_rookie: 1, price_credits: 7, hot_points_7d: 33 },
      ],
    },
  },
  "fantasy/portal/pool": {
    status: "Success",
    data: {
      reps: [
        { rep_recid: "recPV01", rep_name: "Sample Rep One", is_rookie: 0, price_credits: 38 },
        { rep_recid: "recPV02", rep_name: "Sample Rep Two", is_rookie: 1, price_credits: 11 },
        { rep_recid: "recPV03", rep_name: "Sample Rep Three", is_rookie: 0, price_credits: 27 },
        { rep_recid: "recPV04", rep_name: "Sample Rep Four", is_rookie: 1, price_credits: 9 },
        { rep_recid: "recPV05", rep_name: "Sample Rep Five", is_rookie: 0, price_credits: 22 },
        { rep_recid: "recPV06", rep_name: "Sample Rep Six", is_rookie: 1, price_credits: 8 },
      ],
    },
  },
  // #44 — player "trading cards". A drafted roster (so the Roster tab shows
  // cards, not the draft board) plus the rep-cards metadata join. All fictitious.
  "fantasy/portal/mine": {
    status: "Success",
    data: {
      seasonId: "2026-Summer",
      salaryCap: 100,
      rosterSize: 5,
      spent: 96,
      manager: { draftedAt: `${PREVIEW_YDAY}T12:00:00Z`, swapUsed: false },
      roster: [
        { slot: 1, rep_recid: "recPV01", rep_name: "Sample Rep One", is_rookie: 0, price_credits: 38 },
        { slot: 2, rep_recid: "recPV03", rep_name: "Sample Rep Three", is_rookie: 0, price_credits: 27 },
        { slot: 3, rep_recid: "recPV02", rep_name: "Sample Rep Two", is_rookie: 1, price_credits: 11 },
        { slot: 4, rep_recid: "recPV04", rep_name: "Sample Rep Four", is_rookie: 1, price_credits: 9 },
        { slot: 5, rep_recid: "recPV06", rep_name: "Sample Rep Six", is_rookie: 1, price_credits: 8 },
      ],
    },
  },
  // Player-card metadata keyed by rep_recid. Photos null on purpose so the
  // initials fallback renders (preview never serves real employee photos);
  // sparks/trends span hot, cold, and flat to show every badge + line state.
  "fantasy/portal/rep-cards": {
    status: "Success",
    data: {
      seasonId: "2026-Summer",
      sparkDays: 14,
      cards: {
        recPV01: { photoUrl: null, market: "Houston", spark: [4, 6, 5, 8, 7, 9, 11, 10, 13, 12, 15, 14, 18, 21], windowPoints: 153, trend: "hot" },
        recPV02: { photoUrl: null, market: "Detroit", spark: [12, 11, 13, 10, 9, 8, 7, 6, 5, 4, 4, 3, 2, 1], windowPoints: 95, trend: "cold" },
        recPV03: { photoUrl: null, market: "Houston", spark: [7, 8, 6, 7, 8, 7, 6, 8, 7, 7, 8, 6, 7, 8], windowPoints: 100, trend: "flat" },
        recPV04: { photoUrl: null, market: "Detroit", spark: [0, 0, 1, 0, 2, 3, 2, 5, 4, 7, 6, 9, 11, 14], windowPoints: 64, trend: "hot" },
        recPV05: { photoUrl: null, market: "Houston", spark: [9, 8, 9, 7, 6, 5, 6, 4, 5, 3, 4, 2, 3, 2], windowPoints: 73, trend: "cold" },
        recPV06: { photoUrl: null, market: "Detroit", spark: [], windowPoints: 0, trend: "flat" },
      },
    },
  },
  "admin/rep-ledger": {
    status: "Success",
    data: {
      rep: {
        repName: "Sample Rep One",
        isRookie: false,
        priceCredits: 38,
        trailingRev: 184000,
        rookieMultiplier: 1,
      },
      totalPoints: 162,
      lines: [
        { score_date: PREVIEW_YDAY, metric: "dm", raw_stat: 14, points: 14 },
        { score_date: PREVIEW_YDAY, metric: "clutch", raw_stat: 2, points: 30 },
        { score_date: PREVIEW_YDAY, metric: "big_sale", raw_stat: 1, points: 20 },
        { score_date: PREVIEW_TODAY, metric: "dm", raw_stat: 18, points: 18 },
        { score_date: PREVIEW_TODAY, metric: "same_day", raw_stat: 3, points: 30 },
        { score_date: PREVIEW_TODAY, metric: "personal_best", raw_stat: 1, points: 25 },
        { score_date: PREVIEW_TODAY, metric: "pin_timing", raw_stat: 4, points: 10 },
        { score_date: PREVIEW_TODAY, metric: "streak", raw_stat: 3, points: 15 },
      ],
      draftedBy: [
        { manager_id: 1, display_name: "Preview Manager Ada", slot: 1, price_credits: 38 },
        { manager_id: 3, display_name: "Preview Manager Cleo", slot: 2, price_credits: 38 },
      ],
    },
  },
  "admin/rosters": {
    status: "Success",
    data: {
      salaryCap: 100,
      managers: [
        {
          managerId: 1,
          displayName: "Preview Manager Ada",
          employeeRecid: "recPVM01",
          swapUsed: false,
          ranks: { total: 1, perActiveDay: 2, motw: 1 },
          spent: 96,
          creditsLeft: 4,
          draftedAt: `${PREVIEW_YDAY}T12:00:00Z`,
          roster: [
            { slot: 1, rep_name: "Sample Rep One", is_rookie: 0, price_credits: 38 },
            { slot: 2, rep_name: "Sample Rep Three", is_rookie: 0, price_credits: 27 },
            { slot: 3, rep_name: "Sample Rep Two", is_rookie: 1, price_credits: 11 },
            { slot: 4, rep_name: "Sample Rep Four", is_rookie: 1, price_credits: 9 },
            { slot: 5, rep_name: "Sample Rep Six", is_rookie: 1, price_credits: 8 },
          ],
        },
        {
          managerId: 2,
          displayName: "Preview Manager Bex",
          employeeRecid: "recPVM02",
          swapUsed: true,
          ranks: { total: 2, perActiveDay: 1, motw: 3 },
          spent: 88,
          creditsLeft: 12,
          draftedAt: `${PREVIEW_TODAY}T08:30:00Z`,
          roster: [
            { slot: 1, rep_name: "Sample Rep Three", is_rookie: 0, price_credits: 27 },
            { slot: 2, rep_name: "Sample Rep Five", is_rookie: 0, price_credits: 22 },
            { slot: 3, rep_name: "Sample Rep Two", is_rookie: 1, price_credits: 11 },
            { slot: 4, rep_name: "Sample Rep Four", is_rookie: 1, price_credits: 9 },
            { slot: 5, rep_name: "Sample Rep Six", is_rookie: 1, price_credits: 8 },
          ],
        },
        {
          managerId: 3,
          displayName: "Preview Manager Cleo",
          employeeRecid: "recPVM03",
          swapUsed: false,
          ranks: { total: 3, perActiveDay: 5, motw: 2 },
          spent: 79,
          creditsLeft: 21,
          draftedAt: `${PREVIEW_YDAY}T15:45:00Z`,
          roster: [
            { slot: 1, rep_name: "Sample Rep One", is_rookie: 0, price_credits: 38 },
            { slot: 2, rep_name: "Sample Rep Two", is_rookie: 1, price_credits: 11 },
            { slot: 3, rep_name: "Sample Rep Four", is_rookie: 1, price_credits: 9 },
            { slot: 4, rep_name: "Sample Rep Six", is_rookie: 1, price_credits: 8 },
            { slot: 5, rep_name: "Sample Rep Five", is_rookie: 0, price_credits: 13 },
          ],
        },
      ],
    },
  },
  // #29 — admin drill-in standings (all fictitious sample data).
  "admin/standings": {
    status: "Success",
    data: {
      seasonId: "2026-Summer",
      total: [
        { manager_id: 1, employee_recid: "recMgrA", display_name: "Avery Stone", drafted_at: "2026-06-01", total_points: 184.5 },
        { manager_id: 2, employee_recid: "recMgrB", display_name: "Blair Nguyen", drafted_at: "2026-06-02", total_points: 152.0 },
        { manager_id: 3, employee_recid: "recMgrC", display_name: "Casey Rivera", drafted_at: "2026-06-03", total_points: 97.5 },
      ],
      perActiveDay: [
        { manager_id: 2, employee_recid: "recMgrB", display_name: "Blair Nguyen", drafted_at: "2026-06-02", total_points: 152.0, active_days: 7, points_per_day: 21.7 },
        { manager_id: 1, employee_recid: "recMgrA", display_name: "Avery Stone", drafted_at: "2026-06-01", total_points: 184.5, active_days: 8, points_per_day: 23.1 },
      ],
      motw: {
        weekStart: "2026-06-08",
        standings: [
          { manager_id: 1, employee_recid: "recMgrA", display_name: "Avery Stone", dm_points: 12, clutch_points: 8, streak_points: 4, points: 24.0 },
          { manager_id: 3, employee_recid: "recMgrC", display_name: "Casey Rivera", dm_points: 6, clutch_points: 4, streak_points: 2, points: 12.0 },
        ],
      },
    },
  },
  "admin/manager-contribution": {
    status: "Success",
    data: {
      seasonId: "2026-Summer",
      manager: { managerId: 1, employeeRecid: "recMgrA", displayName: "Avery Stone", draftedAt: "2026-06-01", swapUsed: false, status: "active" },
      totalPoints: 184.5,
      reps: [
        { slot: 1, repRecid: "recRep1", repName: "Jordan Lee", isRookie: false, priceCredits: 30, points: 62.0, byMetric: [
          { metric: "dm", points: 40.0, rawStat: 40, firstDate: "2026-06-01", lastDate: "2026-06-07" },
          { metric: "clutch", points: 22.0, rawStat: 11, firstDate: "2026-06-02", lastDate: "2026-06-06" },
        ] },
        { slot: 2, repRecid: "recRep2", repName: "Sam Patel", isRookie: true, priceCredits: 18, points: 48.5, byMetric: [
          { metric: "dm", points: 33.5, rawStat: 24, firstDate: "2026-06-01", lastDate: "2026-06-07" },
          { metric: "big_sale", points: 15.0, rawStat: 3, firstDate: "2026-06-04", lastDate: "2026-06-04" },
        ] },
        { slot: 3, repRecid: "recRep3", repName: "Riley Kim", isRookie: false, priceCredits: 22, points: 41.0, byMetric: [
          { metric: "dm", points: 28.0, rawStat: 28, firstDate: "2026-06-01", lastDate: "2026-06-07" },
          { metric: "streak", points: 13.0, rawStat: 13, firstDate: "2026-06-03", lastDate: "2026-06-07" },
        ] },
        { slot: 4, repRecid: "recRep4", repName: "Devin Cruz", isRookie: false, priceCredits: 16, points: 21.0, byMetric: [
          { metric: "dm", points: 21.0, rawStat: 21, firstDate: "2026-06-01", lastDate: "2026-06-06" },
        ] },
        { slot: 5, repRecid: "recRep5", repName: "Morgan Diaz", isRookie: true, priceCredits: 14, points: 12.0, byMetric: [
          { metric: "same_day", points: 12.0, rawStat: 4, firstDate: "2026-06-02", lastDate: "2026-06-05" },
        ] },
      ],
    },
  },
  // #58 Conditional Branches — the Workflow page preview rendered as a decision
  // tree. `list` shows one saved workflow; opening it returns a preview whose
  // stages include a conditional fork (yes_stages / no_stages) so the
  // WorkflowBranchTree renders instead of the flat list. All fictitious.
  "workflow/portal/list": {
    status: "Success",
    data: [
      {
        id: "wf-preview-58",
        title: "New Rep Onboarding Agreement",
        status: "ready",
        updatedAt: `${PREVIEW_TODAY}T09:30:00Z`,
        createdAt: `${PREVIEW_YDAY}T14:00:00Z`,
      },
    ],
  },
  // Matches both GET /workflow/portal/wf-preview-58 and …/status (substring),
  // returning the full workflow with a branching preview. Status "ready" so the
  // hook doesn't poll.
  "workflow/portal/wf-preview-58": {
    status: "Success",
    data: {
      id: "wf-preview-58",
      title: "New Rep Onboarding Agreement",
      status: "ready",
      updatedAt: `${PREVIEW_TODAY}T09:30:00Z`,
      createdAt: `${PREVIEW_YDAY}T14:00:00Z`,
      preview: {
        summary:
          "When HR marks a new rep hired, they're texted their agreement to e-sign. If they don't sign within a day, we nudge them and loop in their manager; once signed, the VP counter-signs and everyone gets the finished copy.",
        stages: [
          {
            n: 1,
            actor: "Rep",
            title: "Gets the agreement",
            detail: "Texted a secure link the moment HR marks them hired.",
            timing: "on hire",
          },
          {
            n: 2,
            actor: "Rep",
            title: "Signs on their phone",
            detail: "Taps the link and e-signs — no app, no printer.",
          },
          {
            type: "conditional",
            condition: "If the rep hasn't signed within 24 hours",
            yes_stages: [
              {
                actor: "Rep",
                title: "Gets a friendly reminder",
                detail: "An automatic text nudge with the same secure link.",
                timing: "24 hours after sending",
              },
              {
                actor: "Recruiting Manager",
                title: "Is looped in",
                detail:
                  "Still unsigned after the reminder? Their manager gets a heads-up to follow up personally.",
                timing: "48 hours after sending",
              },
            ],
            no_stages: [
              {
                actor: "VP of Sales",
                title: "Counter-signs",
                detail: "Asked to sign the moment the rep does.",
                timing: "within minutes",
              },
              {
                actor: "HR",
                title: "Gets the signed copy",
                detail:
                  "The finished PDF is filed on the rep's record and a copy is texted to the rep.",
                timing: "once fully signed",
              },
            ],
          },
        ],
      },
      prerequisites: [],
      messages: [],
    },
  },

  // Feature #69 — Dynamic Variables Explorer. Sample systems/tables/fields so the
  // right-side panel renders its accordion + copy buttons. All fictitious shape.
  "workflow/portal/variables": {
    status: "Success",
    data: {
      systems: [
        {
          name: "Employees",
          source: "airtable",
          tables: [
            {
              name: "Employee",
              fields: [
                { name: "First Name", column: "first_name", type: "singleLineText", token: "{Employees.first_name}" },
                { name: "Last Name", column: "last_name", type: "singleLineText", token: "{Employees.last_name}" },
                { name: "Position", column: "position", type: "multipleRecordLinks", token: "{Employees.position}" },
                { name: "Start Date", column: "start_date", type: "date", token: "{Employees.start_date}" },
              ],
            },
            {
              name: "Onboarding Tasks",
              fields: [
                { name: "Task Name", column: "task_name", type: "singleLineText", token: "{Employees.task_name}" },
                { name: "Status", column: "status", type: "singleSelect", token: "{Employees.status}" },
              ],
            },
          ],
        },
        {
          name: "Sales",
          source: "airtable",
          tables: [
            {
              name: "Leads",
              fields: [
                { name: "Lead Name", column: "lead_name", type: "singleLineText", token: "{Sales.lead_name}" },
                { name: "Stage", column: "stage", type: "singleSelect", token: "{Sales.stage}" },
              ],
            },
          ],
        },
        {
          name: "Portal Database (PestRoutes)",
          source: "mysql",
          tables: [
            {
              name: "Customers",
              table: "customer",
              fields: [
                { name: "fname", column: "fname", type: "", token: "{customer.fname}" },
                { name: "lname", column: "lname", type: "", token: "{customer.lname}" },
                { name: "email", column: "email", type: "", token: "{customer.email}" },
              ],
            },
            {
              name: "Appointments",
              table: "appointments",
              fields: [
                { name: "appointmentID", column: "appointmentID", type: "", token: "{appointments.appointmentID}" },
                { name: "date", column: "date", type: "", token: "{appointments.date}" },
              ],
            },
          ],
        },
      ],
      builtAt: PREVIEW_TODAY + "T00:00:00.000Z",
    },
  },

  // --- Tournament Admin (location-filter fix test) -------------------------
  // Mock data to let QA test the participant location filter in the create
  // wizard without a live BFF. Employees span both markets so clicking the
  // Detroit chip should trim the roster to Detroit Route Managers + Team
  // Leaders only.
  "tournament/portal/list": { status: "Success", data: [] },
  "tournament/portal/admin/positions": {
    status: "Success",
    data: {
      positions: [
        { id: "recPosRM001", name: "Route Manager" },
        { id: "recPosTL001", name: "Team Leader" },
        { id: "recPosSR001", name: "Sales Rep" },
      ],
    },
  },
  "tournament/portal/admin/templates": { status: "Success", data: [] },
  "tournament/portal/admin/check-conflicts": { status: "Success", data: [] },
  "tournament/portal/preview-seeding": { status: "Success", data: { rows: [], warnings: [], windowStart: null, windowEnd: null, seedingRollup: null } },
  "get-all-active-employees": {
    status: "Success",
    data: [
      { id: "recHouRM1", name: "Ben Houston", location: "Houston", position: ["Route Manager"] },
      { id: "recHouRM2", name: "Carlos Houston", location: "Houston", position: ["Route Manager"] },
      { id: "recHouRM3", name: "Dana Houston", location: "Houston", position: ["Route Manager"] },
      { id: "recHouSR1", name: "Eva Houston", location: "Houston", position: ["Sales Rep"] },
      { id: "recDetRM1", name: "Frank Detroit", location: "Detroit", position: ["Route Manager"] },
      { id: "recDetRM2", name: "Grace Detroit", location: "Detroit", position: ["Route Manager"] },
      { id: "recDetTL1", name: "Henry Detroit", location: "Detroit", position: ["Team Leader"] },
      { id: "recDetTL2", name: "Iris Detroit", location: "Detroit", position: ["Team Leader"] },
      { id: "recDetSR1", name: "James Detroit", location: "Detroit", position: ["Sales Rep"] },
    ],
  },
};

// --- Workflow page (feature #64 — Organization & Filter) -------------------
// Five sample workflows with varied creators / positions / locations so the
// filter bar (Position / Individual / Location / keyword) has something to act
// on. All fictitious — UI shell only.
const WORKFLOW_LIST = [
  { id: 1, title: "New hire onboarding texts", scribble: "Text every new rep their first-day checklist.", status: "active", createdByRecId: "recPREVLevi", creatorName: "Levi Leatham", creatorPosition: "President", location: "Houston", createdAt: new Date(Date.now() - 6 * 86400000).toISOString(), updatedAt: new Date(Date.now() - 2 * 86400000).toISOString() },
  { id: 2, title: "Detroit route reminder emails", scribble: "Email Detroit techs their route the night before.", status: "preview_ready", createdByRecId: "recPREVJamie", creatorName: "Jamie Carter", creatorPosition: "Branch Manager", location: "Detroit", createdAt: new Date(Date.now() - 5 * 86400000).toISOString(), updatedAt: new Date(Date.now() - 1 * 86400000).toISOString() },
  { id: 3, title: "Weekly recruiting pipeline digest", scribble: "Summarize new recruits every Monday.", status: "creating", createdByRecId: "recPREVMorgan", creatorName: "Morgan Diaz", creatorPosition: "Route Manager", location: "Houston", createdAt: new Date(Date.now() - 4 * 86400000).toISOString(), updatedAt: new Date(Date.now() - 4 * 86400000).toISOString() },
  { id: 4, title: "Customer review request flow", scribble: "Ask for a Google review after a 5-star service.", status: "queued", createdByRecId: "recPREVAvery", creatorName: "Avery Stone", creatorPosition: "Retention Specialist", location: "Detroit", createdAt: new Date(Date.now() - 3 * 86400000).toISOString(), updatedAt: new Date(Date.now() - 3 * 86400000).toISOString() },
  { id: 5, title: "Onboarding checklist auto-assign", scribble: "Auto-assign onboarding tasks when a hire is added.", status: "error", createdByRecId: "recPREVLevi", creatorName: "Levi Leatham", creatorPosition: "President", location: "Houston", createdAt: new Date(Date.now() - 2 * 86400000).toISOString(), updatedAt: new Date(Date.now() - 12 * 3600000).toISOString() },
];

// Filter the mock list by the same query params the real BFF honors, so the
// preview's filter bar visibly narrows results (not just renders).
function filterWorkflowList(url) {
  const qs = url.indexOf("?") !== -1 ? url.slice(url.indexOf("?") + 1) : "";
  const params = new URLSearchParams(qs);
  const position = (params.get("position") || "").trim();
  const recId = (params.get("created_by_recid") || "").trim();
  const location = (params.get("location") || "").trim();
  const q = (params.get("q") || "").trim().toLowerCase();
  const rows = WORKFLOW_LIST.filter((w) => {
    if (position && w.creatorPosition !== position) return false;
    if (recId && w.createdByRecId !== recId) return false;
    if (location && location.toLowerCase() !== "all" && w.location !== location) return false;
    if (q && !(`${w.title} ${w.scribble}`.toLowerCase().includes(q))) return false;
    return true;
  });
  return { status: "Success", data: rows };
}

export function getFixture(url) {
  if (typeof url !== "string") return DEFAULT_RESPONSE;
  // Workflow list (feature #64) — filtered client-side from the mock set so the
  // filter bar demonstrably narrows results. Checked before the generic loop;
  // the facets URL ("/filters") doesn't contain "/list" so it falls through.
  if (url.indexOf("workflow/portal/list") !== -1) {
    return filterWorkflowList(url);
  }
  // App.js calls get-user-info on load + every 7 min; a failure there trips
  // the session-expiry toast + logout, bouncing the preview to login. Keep it
  // alive with the mock user. Resolved here (not in FIXTURES) to avoid a
  // module-init dependency on previewMode during the circular import.
  if (url.indexOf("get-user-info") !== -1) {
    return { status: "Success", data: MOCK_USER_INFO };
  }
  for (const key of Object.keys(FIXTURES)) {
    if (url.indexOf(key) !== -1) return FIXTURES[key];
  }
  return DEFAULT_RESPONSE;
}
